import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FileText, Shield, Zap, ArrowRight, Users, 
  CheckCircle, Star, Instagram, MapPin, Phone,
  Mail, Heart, ChevronRight, Sparkles, Lock, Bot,
  Menu, X, Upload, Loader2, Eye, ChevronDown, Target,
  TrendingUp, SearchCheck, ScanSearch, Home, Clock
} from 'lucide-react';
import axios from 'axios';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { ToulouseSignature } from '../components/shared/ToulouseSignature';
import { ProactiveChatBubble } from '../components/shared/ProactiveChatBubble';
import AssistantDuBug from '../components/shared/AssistantDuBug';
import StructuredData from '../components/layout/StructuredData';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_contract-cleaner/artifacts/7lw9nr1y_logo%20l%27effaceur.jpeg";

// ============ CIRCUIT ANIMATIONS SYSTÈME ============

// Circuit Pattern Animation Principal (Header)
const CircuitHeader = () => (
  <div className="absolute top-16 left-0 right-0 h-24 overflow-hidden pointer-events-none z-0 opacity-70">
    <svg className="w-full h-full" viewBox="0 0 1920 96" preserveAspectRatio="xMidYMid slice">
      <defs>
        <linearGradient id="circuit-gradient-cyan" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#00F5FF" stopOpacity="0" />
          <stop offset="50%" stopColor="#00F5FF" stopOpacity="0.8" />
          <stop offset="100%" stopColor="#00F5FF" stopOpacity="0" />
        </linearGradient>
        <linearGradient id="circuit-gradient-blue" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#1E3A8A" stopOpacity="0" />
          <stop offset="50%" stopColor="#1E3A8A" stopOpacity="0.6" />
          <stop offset="100%" stopColor="#1E3A8A" stopOpacity="0" />
        </linearGradient>
        <filter id="glow-cyan">
          <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
          <feMerge><feMergeNode in="coloredBlur"/><feMergeNode in="SourceGraphic"/></feMerge>
        </filter>
      </defs>
      
      <g className="circuit-left">
        <path d="M0 48 L200 48 L250 24 L400 24 L450 48 L600 48 L700 60 L850 60 L900 48 L960 48" stroke="url(#circuit-gradient-cyan)" strokeWidth="1.5" fill="none" filter="url(#glow-cyan)" opacity="0.5">
          <animate attributeName="stroke-dasharray" from="0 2000" to="2000 0" dur="4s" repeatCount="indefinite" />
        </path>
        <path d="M0 24 L150 24 L200 36 L350 36 L400 48 L550 48 L650 36 L800 36 L850 48 L960 48" stroke="url(#circuit-gradient-blue)" strokeWidth="1" fill="none" opacity="0.3">
          <animate attributeName="stroke-dasharray" from="0 2000" to="2000 0" dur="5s" repeatCount="indefinite" />
        </path>
        <circle cx="250" cy="24" r="3" fill="#00F5FF" opacity="0.6"><animate attributeName="opacity" values="0.2;0.8;0.2" dur="2.5s" repeatCount="indefinite" /></circle>
        <circle cx="700" cy="60" r="3" fill="#1E3A8A" opacity="0.5"><animate attributeName="opacity" values="0.3;0.7;0.3" dur="2s" repeatCount="indefinite" /></circle>
      </g>
      
      <g className="circuit-right">
        <path d="M1920 48 L1720 48 L1670 24 L1520 24 L1470 48 L1320 48 L1220 60 L1070 60 L1020 48 L960 48" stroke="url(#circuit-gradient-cyan)" strokeWidth="1.5" fill="none" filter="url(#glow-cyan)" opacity="0.5">
          <animate attributeName="stroke-dasharray" from="0 2000" to="2000 0" dur="4s" repeatCount="indefinite" />
        </path>
        <path d="M1920 24 L1770 24 L1720 36 L1570 36 L1520 48 L1370 48 L1270 36 L1120 36 L1070 48 L960 48" stroke="url(#circuit-gradient-blue)" strokeWidth="1" fill="none" opacity="0.3">
          <animate attributeName="stroke-dasharray" from="0 2000" to="2000 0" dur="5s" repeatCount="indefinite" />
        </path>
        <circle cx="1670" cy="24" r="3" fill="#00F5FF" opacity="0.6"><animate attributeName="opacity" values="0.2;0.8;0.2" dur="2.5s" repeatCount="indefinite" /></circle>
      </g>
      
      <circle cx="960" cy="48" r="5" fill="#00F5FF" opacity="0.7">
        <animate attributeName="r" values="4;8;4" dur="3s" repeatCount="indefinite" />
        <animate attributeName="opacity" values="0.4;0.9;0.4" dur="3s" repeatCount="indefinite" />
      </circle>
    </svg>
  </div>
);

// Grands circuits horizontaux pour sections (comme le header mais plus grands)
const CircuitSectionDivider = ({ invert = false }) => (
  <div className={`absolute ${invert ? 'bottom-0' : 'top-0'} left-0 right-0 h-16 overflow-hidden pointer-events-none opacity-40`}>
    <svg className="w-full h-full" viewBox="0 0 1920 64" preserveAspectRatio="xMidYMid slice">
      <path d={invert 
        ? "M0 48 L300 48 L350 32 L500 32 L550 48 L800 48 L900 32 L1020 32 L1100 48 L1400 48 L1450 32 L1600 32 L1650 48 L1920 48"
        : "M0 16 L300 16 L350 32 L500 32 L550 16 L800 16 L900 32 L1020 32 L1100 16 L1400 16 L1450 32 L1600 32 L1650 16 L1920 16"
      } stroke="#00F5FF" strokeWidth="1.5" fill="none" opacity="0.6">
        <animate attributeName="stroke-dasharray" from="0 3000" to="3000 0" dur="6s" repeatCount="indefinite" />
      </path>
      <circle cx="350" cy="32" r="4" fill="#00F5FF" opacity="0.7"><animate attributeName="opacity" values="0.3;1;0.3" dur="2s" repeatCount="indefinite" /></circle>
      <circle cx="900" cy="32" r="4" fill="#00F5FF" opacity="0.7"><animate attributeName="opacity" values="0.4;1;0.4" dur="2.5s" repeatCount="indefinite" /></circle>
      <circle cx="1450" cy="32" r="4" fill="#00F5FF" opacity="0.7"><animate attributeName="opacity" values="0.3;0.9;0.3" dur="1.8s" repeatCount="indefinite" /></circle>
    </svg>
  </div>
);

// Circuits latéraux animés pour bordures de sections
const CircuitSideBorder = ({ side = 'left' }) => (
  <div className={`absolute ${side === 'left' ? 'left-0' : 'right-0'} top-0 bottom-0 w-16 overflow-hidden pointer-events-none opacity-30`}>
    <svg className="h-full w-full" viewBox="0 0 64 800" preserveAspectRatio="xMidYMid slice">
      <defs>
        <linearGradient id={`circuit-side-${side}`} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#00F5FF" stopOpacity="0" />
          <stop offset="20%" stopColor="#00F5FF" stopOpacity="0.8" />
          <stop offset="80%" stopColor="#00F5FF" stopOpacity="0.8" />
          <stop offset="100%" stopColor="#00F5FF" stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={side === 'left'
        ? "M32 0 L32 100 L16 130 L16 200 L32 230 L32 350 L48 380 L48 450 L32 480 L32 600 L16 630 L16 700 L32 730 L32 800"
        : "M32 0 L32 100 L48 130 L48 200 L32 230 L32 350 L16 380 L16 450 L32 480 L32 600 L48 630 L48 700 L32 730 L32 800"
      } stroke={`url(#circuit-side-${side})`} strokeWidth="2" fill="none">
        <animate attributeName="stroke-dasharray" from="0 1600" to="1600 0" dur="8s" repeatCount="indefinite" />
      </path>
      <circle cx={side === 'left' ? '16' : '48'} cy="130" r="4" fill="#00F5FF"><animate attributeName="opacity" values="0.3;1;0.3" dur="2s" repeatCount="indefinite" /></circle>
      <circle cx={side === 'left' ? '48' : '16'} cy="380" r="4" fill="#00F5FF"><animate attributeName="opacity" values="0.4;1;0.4" dur="2.5s" repeatCount="indefinite" /></circle>
      <circle cx={side === 'left' ? '16' : '48'} cy="630" r="4" fill="#00F5FF"><animate attributeName="opacity" values="0.3;0.9;0.3" dur="1.8s" repeatCount="indefinite" /></circle>
    </svg>
  </div>
);

// Grand circuit de fond pour Hero Section
const CircuitHeroBackground = () => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none opacity-20">
    <svg className="w-full h-full" viewBox="0 0 1920 1080" preserveAspectRatio="xMidYMid slice">
      <defs>
        <filter id="glow-hero">
          <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
          <feMerge><feMergeNode in="coloredBlur"/><feMergeNode in="SourceGraphic"/></feMerge>
        </filter>
      </defs>
      {/* Circuit principal horizontal */}
      <path d="M-100 400 L200 400 L280 350 L500 350 L580 400 L900 400 L960 350 L1020 400 L1340 400 L1420 350 L1640 350 L1720 400 L2020 400" stroke="#00F5FF" strokeWidth="2" fill="none" filter="url(#glow-hero)">
        <animate attributeName="stroke-dasharray" from="0 4000" to="4000 0" dur="10s" repeatCount="indefinite" />
      </path>
      {/* Circuit secondaire */}
      <path d="M-100 600 L300 600 L380 650 L600 650 L680 600 L1000 600 L1060 650 L1120 600 L1440 600 L1520 650 L1740 650 L1820 600 L2020 600" stroke="#1E3A8A" strokeWidth="1.5" fill="none" opacity="0.5">
        <animate attributeName="stroke-dasharray" from="0 4000" to="4000 0" dur="12s" repeatCount="indefinite" />
      </path>
      {/* Connexions verticales */}
      <path d="M500 350 L500 250 L600 200 L600 100" stroke="#00F5FF" strokeWidth="1.5" fill="none">
        <animate attributeName="stroke-dasharray" from="0 500" to="500 0" dur="4s" repeatCount="indefinite" />
      </path>
      <path d="M1420 350 L1420 250 L1320 200 L1320 100" stroke="#00F5FF" strokeWidth="1.5" fill="none">
        <animate attributeName="stroke-dasharray" from="0 500" to="500 0" dur="4.5s" repeatCount="indefinite" />
      </path>
      <path d="M600 650 L600 750 L700 800 L700 900" stroke="#00F5FF" strokeWidth="1.5" fill="none">
        <animate attributeName="stroke-dasharray" from="0 500" to="500 0" dur="3.5s" repeatCount="indefinite" />
      </path>
      <path d="M1320 650 L1320 750 L1220 800 L1220 900" stroke="#00F5FF" strokeWidth="1.5" fill="none">
        <animate attributeName="stroke-dasharray" from="0 500" to="500 0" dur="5s" repeatCount="indefinite" />
      </path>
      {/* Noeuds lumineux */}
      <circle cx="500" cy="350" r="6" fill="#00F5FF"><animate attributeName="opacity" values="0.4;1;0.4" dur="2s" repeatCount="indefinite" /></circle>
      <circle cx="960" cy="350" r="8" fill="#00F5FF"><animate attributeName="r" values="6;12;6" dur="3s" repeatCount="indefinite" /><animate attributeName="opacity" values="0.5;1;0.5" dur="3s" repeatCount="indefinite" /></circle>
      <circle cx="1420" cy="350" r="6" fill="#00F5FF"><animate attributeName="opacity" values="0.3;0.9;0.3" dur="2.5s" repeatCount="indefinite" /></circle>
      <circle cx="600" cy="650" r="5" fill="#1E3A8A"><animate attributeName="opacity" values="0.4;0.8;0.4" dur="2s" repeatCount="indefinite" /></circle>
      <circle cx="1320" cy="650" r="5" fill="#1E3A8A"><animate attributeName="opacity" values="0.3;0.7;0.3" dur="1.8s" repeatCount="indefinite" /></circle>
    </svg>
  </div>
);

// Micro-circuits aux coins des sections
const CircuitCorner = ({ position = 'top-left' }) => {
  const positions = {
    'top-left': 'top-0 left-0',
    'top-right': 'top-0 right-0 rotate-90',
    'bottom-left': 'bottom-0 left-0 -rotate-90',
    'bottom-right': 'bottom-0 right-0 rotate-180'
  };
  return (
    <div className={`absolute ${positions[position]} w-24 h-24 overflow-hidden pointer-events-none opacity-40`}>
      <svg className="w-full h-full" viewBox="0 0 96 96">
        <path d="M0 48 L20 48 L30 30 L48 30 L48 0" stroke="#00F5FF" strokeWidth="1.5" fill="none">
          <animate attributeName="stroke-dasharray" from="0 150" to="150 0" dur="3s" repeatCount="indefinite" />
        </path>
        <path d="M0 24 L10 24 L20 40 L30 40" stroke="#00F5FF" strokeWidth="1" fill="none" opacity="0.5">
          <animate attributeName="stroke-dasharray" from="0 100" to="100 0" dur="2.5s" repeatCount="indefinite" />
        </path>
        <circle cx="30" cy="30" r="3" fill="#00F5FF"><animate attributeName="opacity" values="0.3;1;0.3" dur="2s" repeatCount="indefinite" /></circle>
      </svg>
    </div>
  );
};

// Micro-animation circuits pour sections (subtiles, fluides)
const CircuitAccent = ({ position = 'left', delay = 0 }) => (
  <div className={`absolute ${position === 'left' ? 'left-0' : 'right-0'} top-1/2 -translate-y-1/2 w-32 h-64 overflow-hidden pointer-events-none opacity-30`}>
    <svg className="w-full h-full" viewBox="0 0 128 256" preserveAspectRatio="xMidYMid slice">
      <defs>
        <linearGradient id={`circuit-accent-${position}-${delay}`} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#00F5FF" stopOpacity="0" />
          <stop offset="50%" stopColor="#00F5FF" stopOpacity="0.6" />
          <stop offset="100%" stopColor="#00F5FF" stopOpacity="0" />
        </linearGradient>
      </defs>
      <path 
        d={position === 'left' 
          ? "M0 128 L40 128 L50 100 L50 80 L70 60 L70 40 L90 20 M50 100 L50 156 L70 176 L70 196 L90 216"
          : "M128 128 L88 128 L78 100 L78 80 L58 60 L58 40 L38 20 M78 100 L78 156 L58 176 L58 196 L38 216"
        } 
        stroke={`url(#circuit-accent-${position}-${delay})`} 
        strokeWidth="2" 
        fill="none"
      >
        <animate attributeName="stroke-dasharray" from="0 500" to="500 0" dur={`${3 + delay}s`} repeatCount="indefinite" />
      </path>
      <circle cx={position === 'left' ? '50' : '78'} cy="100" r="4" fill="#00F5FF">
        <animate attributeName="opacity" values="0.2;0.8;0.2" dur={`${2 + delay * 0.5}s`} repeatCount="indefinite" />
      </circle>
    </svg>
  </div>
);

// Circuit Watermark Background (Subtle pattern)
const CircuitWatermark = () => (
  <div className="absolute inset-0 pointer-events-none overflow-hidden opacity-[0.04]">
    <svg className="w-full h-full" viewBox="0 0 400 400" preserveAspectRatio="xMidYMid slice">
      <pattern id="circuit-pattern" x="0" y="0" width="100" height="100" patternUnits="userSpaceOnUse">
        <path d="M0 50 L30 50 L40 30 L60 30 L70 50 L100 50" stroke="#00F5FF" strokeWidth="1" fill="none" />
        <path d="M50 0 L50 30 L30 40 L30 60 L50 70 L50 100" stroke="#00F5FF" strokeWidth="1" fill="none" />
        <circle cx="40" cy="30" r="3" fill="#00F5FF" />
        <circle cx="70" cy="50" r="2" fill="#00F5FF" />
        <circle cx="30" cy="60" r="3" fill="#00F5FF" />
      </pattern>
      <rect width="100%" height="100%" fill="url(#circuit-pattern)" />
    </svg>
  </div>
);

// Mobile Menu Component
const MobileMenu = ({ isOpen, setIsOpen }) => (
  <AnimatePresence>
    {isOpen && (
      <>
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/90 z-50 md:hidden" onClick={() => setIsOpen(false)} />
        <motion.div initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }} transition={{ type: 'tween' }}
          className="fixed right-0 top-0 bottom-0 w-72 bg-void border-l border-gray-800 z-50 overflow-y-auto">
          <div className="p-4 border-b border-gray-800 flex justify-between items-center">
            <span className="font-orbitron text-xl font-black tracking-wider">
              <span className="text-cyan-400">L'</span>
              <span className="text-white">EFFACEUR</span>
            </span>
            <button onClick={() => setIsOpen(false)} className="text-gray-400 p-2"><X className="w-6 h-6" /></button>
          </div>
          
          <nav className="p-4 space-y-2">
            <Link to="/login" onClick={() => setIsOpen(false)} className="flex items-center gap-3 px-4 py-4 text-gray-300 hover:bg-gray-800 transition-all">
              <Lock className="w-5 h-5 text-cyan-400" />Connexion
            </Link>
            <Link to="/register" onClick={() => setIsOpen(false)} className="flex items-center gap-3 px-4 py-4 text-cyan-400 bg-cyan-400/10 border border-cyan-400/30">
              <Sparkles className="w-5 h-5" />Créer un compte
            </Link>
          </nav>
          
          <div className="p-4 border-t border-gray-800">
            <p className="text-gray-500 text-xs mb-3 font-mono">// RÉSEAUX</p>
            <div className="space-y-2">
              <a href="https://www.instagram.com/localia_web" target="_blank" rel="noopener noreferrer"
                className="flex items-center gap-3 px-4 py-3 text-gray-400 hover:text-magenta-400 hover:bg-gray-800 transition-all">
                <Instagram className="w-5 h-5" />Instagram
              </a>
              <a href="https://www.tiktok.com/@localia_web" target="_blank" rel="noopener noreferrer"
                className="flex items-center gap-3 px-4 py-3 text-gray-400 hover:text-magenta-400 hover:bg-gray-800 transition-all">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/></svg>
                TikTok
              </a>
              <a href="https://www.google.fr/search?q=L%27EFFACEUR+Toulouse" target="_blank" rel="noopener noreferrer"
                className="flex items-center gap-3 px-4 py-3 text-gray-400 hover:text-cyan-400 hover:bg-gray-800 transition-all">
                <MapPin className="w-5 h-5" />Nous trouver
              </a>
            </div>
          </div>
        </motion.div>
      </>
    )}
  </AnimatePresence>
);

// FAQ Section Component
const FAQSection = () => {
  const [openItem, setOpenItem] = useState(null);
  
  const faqs = [
    {
      q: "Est-ce légal que L'Effaceur résilie à ma place ?",
      a: "Absolument. En signant notre Mandat de Succès, vous nous donnez le pouvoir légal (Art. 1984 du Code Civil) d'agir en votre nom. C'est exactement comme si vous donniez procuration à un professionnel pour gérer une démarche administrative à votre place."
    },
    {
      q: "Combien de temps prend une résiliation ?",
      a: "Une fois le mandat signé, notre automate lance la procédure en moins de 24h. Le délai de prise en compte dépend ensuite de l'organisme (généralement entre 7 et 15 jours). Vous recevrez une notification push dès que la confirmation de résiliation est verrouillée."
    },
    {
      q: "Mes relevés bancaires sont-ils en sécurité ?",
      a: "La sécurité est notre priorité. Vos documents sont analysés par une IA sécurisée via un tunnel de chiffrement AES-256. Aucune donnée n'est vendue à des tiers, et tous vos documents sont automatiquement désintégrés de nos serveurs 30 jours après la fin de la mission."
    }
  ];
  
  return (
    <section className="py-20 px-4 relative" data-testid="faq-section">
      <CircuitWatermark />
      <div className="max-w-3xl mx-auto relative z-10">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} className="text-center mb-12">
          <h2 className="font-orbitron text-3xl md:text-4xl font-bold text-white mb-4">
            QUESTIONS <span className="text-cyan-400">FRÉQUENTES</span>
          </h2>
        </motion.div>
        
        <div className="space-y-4">
          {faqs.map((faq, idx) => (
            <motion.div key={idx} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.1 }}
              className="glass-card border border-gray-800 overflow-hidden">
              <button onClick={() => setOpenItem(openItem === idx ? null : idx)}
                className="w-full flex items-center justify-between p-6 text-left" data-testid={`faq-${idx}`}>
                <span className="text-white font-medium pr-4">{faq.q}</span>
                <ChevronDown className={`w-5 h-5 text-cyan-400 flex-shrink-0 transition-transform ${openItem === idx ? 'rotate-180' : ''}`} />
              </button>
              <AnimatePresence>
                {openItem === idx && (
                  <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }}
                    className="overflow-hidden">
                    <div className="px-6 pb-6 text-gray-400 leading-relaxed border-t border-gray-800 pt-4">{faq.a}</div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Pricing Card avec micro-animation circuit
const PricingCard = ({ pack, isPopular = false }) => {
  // Définir les couleurs selon le type de mission
  const getColors = () => {
    switch(pack.id) {
      case 'mission_audit':
        return { border: 'border-success', glow: 'hover:shadow-[0_0_30px_rgba(0,255,148,0.4)]', text: 'text-success', bg: 'bg-success' };
      case 'mission_demenagement':
        return { border: 'border-cyan-400', glow: 'hover:shadow-[0_0_30px_rgba(0,245,255,0.4)]', text: 'text-cyan-400', bg: 'bg-cyan-400' };
      case 'mission_heritage':
        return { border: 'border-magenta-400', glow: 'hover:shadow-[0_0_30px_rgba(255,0,255,0.4)]', text: 'text-magenta-400', bg: 'bg-magenta-400' };
      default:
        return { border: 'border-cyan-400/30', glow: 'hover:shadow-[0_0_20px_rgba(0,245,255,0.2)]', text: 'text-cyan-400', bg: 'bg-cyan-400' };
    }
  };
  
  const colors = getColors();
  
  // Style uniforme NEON GLOW pour tous les badges
  const getBadgeGlow = () => {
    if (pack.id === 'mission_audit') return 'rgba(0,255,148,0.8)';
    if (pack.id === 'mission_demenagement') return 'rgba(0,245,255,0.8)';
    return 'rgba(255,0,255,0.8)';
  };
  
  const badgeStyle = {
    boxShadow: `0 0 20px ${getBadgeGlow()}, 0 0 40px ${getBadgeGlow().replace('0.8', '0.4')}`,
    textShadow: '0 0 10px rgba(255, 255, 255, 0.5)'
  };
  
  const getCircuitColor = () => {
    if (pack.id === 'mission_audit') return '#00FF94';
    if (pack.id === 'mission_demenagement') return '#00F5FF';
    return '#FF00FF';
  };
  
  return (
    <div className="relative pt-6">
      {/* Badge NEON GLOW uniforme pour tous les packs */}
      <div 
        className={`absolute top-0 left-1/2 -translate-x-1/2 z-20 px-4 py-1.5 text-xs font-orbitron uppercase tracking-wider font-bold whitespace-nowrap flex items-center gap-1.5 ${colors.bg} ${pack.id === 'mission_heritage' ? 'text-white' : 'text-black'}`}
        style={badgeStyle}
      >
        {pack.id === 'mission_audit' && <><Zap className="w-3 h-3" />Pouvoir d'Achat</>}
        {pack.id === 'mission_demenagement' && <><Home className="w-3 h-3" />Populaire</>}
        {pack.id === 'mission_heritage' && <><Star className="w-3 h-3" />Haut Succès</>}
      </div>
      
      <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
        className={`glass-card p-6 md:p-8 border-2 ${colors.border} ${colors.glow} transition-all duration-500 relative group overflow-hidden`}
        data-testid={`pricing-card-${pack.id}`}>
        {/* Micro-circuit animation on hover */}
        <div className="absolute top-0 right-0 w-24 h-24 opacity-0 group-hover:opacity-30 transition-opacity duration-500 pointer-events-none">
          <svg className="w-full h-full" viewBox="0 0 96 96">
            <path d="M96 0 L96 30 L70 30 L60 50 L40 50" stroke={getCircuitColor()} strokeWidth="1" fill="none">
              <animate attributeName="stroke-dasharray" from="0 200" to="200 0" dur="1.5s" repeatCount="indefinite" />
            </path>
            <circle cx="40" cy="50" r="3" fill={getCircuitColor()}><animate attributeName="opacity" values="0.3;1;0.3" dur="1s" repeatCount="indefinite" /></circle>
          </svg>
        </div>
        
        <div className="space-y-5 relative z-10">
          {/* Titre et sous-titre */}
          <div>
            <h3 className={`font-orbitron text-lg md:text-xl font-bold mb-2 ${colors.text}`}>{pack.title}</h3>
            <p className="text-gray-400 text-sm font-mono">{pack.subtitle}</p>
          </div>
          
          {/* Description simplifiée */}
          <p className="text-gray-300 text-sm leading-relaxed border-l-2 pl-3" style={{ borderColor: getCircuitColor() }}>
            {pack.description}
          </p>
          
          {/* Prix — avec prix barré si offre de lancement active */}
          <div className="flex items-baseline gap-2 py-2 flex-wrap">
            {pack.priceStrike && (
              <span className="font-orbitron text-lg text-gray-500 line-through decoration-2 decoration-red-500/60">
                {pack.priceStrike}
              </span>
            )}
            <span className={`font-orbitron text-3xl md:text-4xl font-black ${colors.text}`} style={{ textShadow: `0 0 20px ${getBadgeGlow().replace('0.8', '0.5')}` }}>{pack.price}</span>
            {pack.suffix && <span className="text-gray-400 text-sm">{pack.suffix}</span>}
            {pack.priceStrike && (
              <span className="font-mono text-[10px] text-orange-400 border border-orange-400/40 rounded px-1.5 py-0.5 uppercase">
                Lancement 6 mois
              </span>
            )}
          </div>
          
          {/* Features */}
          <ul className="space-y-2">
            {pack.features.map((feature, idx) => (
              <li key={idx} className="flex items-start gap-2 text-sm">
                <CheckCircle className={`w-4 h-4 flex-shrink-0 mt-0.5 ${colors.text}`} />
                <span className="text-gray-300">{feature}</span>
              </li>
            ))}
          </ul>
          
          {/* CTA - Pack Audit : redirige vers /audit. Autres packs : /register.
              Bouton custom (pas Button shadcn) pour éviter le hover cyan par défaut. */}
          <Link to={pack.id === 'mission_audit' ? '/audit' : '/register'} data-testid={`cta-${pack.id}`}>
            <button
              type="button"
              className={`w-full py-6 font-orbitron uppercase tracking-wider border-2 relative overflow-hidden ${colors.border} bg-transparent transition-all duration-300 group mission-card-button rounded-md text-sm font-medium`}
              style={{ '--btn-color': colors.text.includes('cyan') ? '#00F5FF' : colors.text.includes('magenta') ? '#FF00FF' : '#00FF94' }}
            >
              <span
                className={`relative z-10 flex items-center justify-center transition-colors duration-200 ${colors.text} group-hover:text-white`}
                style={{ textShadow: 'none' }}
              >
                <span className="group-hover:drop-shadow-[0_0_8px_currentColor]">
                  {pack.id === 'mission_audit' ? "Lancer l'Audit gratuit" : 'Lancer la Mission'}
                </span>
                <ArrowRight className="w-4 h-4 ml-2" />
              </span>
              <span
                className={`absolute inset-0 opacity-0 group-hover:opacity-20 transition-opacity duration-300`}
                style={{ backgroundColor: colors.text.includes('cyan') ? '#00F5FF' : colors.text.includes('magenta') ? '#FF00FF' : '#00FF94' }}
              />
            </button>
          </Link>
        </div>
      </motion.div>
    </div>
  );
};

// ============ SAVINGS COUNTER - PREUVE SOCIALE ANIMÉE ============
const SavingsCounter = () => {
  const [totalSavings, setTotalSavings] = useState(127845);
  const [clientCount, setClientCount] = useState(1247);
  const [displaySavings, setDisplaySavings] = useState(127845);
  const [isAnimating, setIsAnimating] = useState(false);
  
  // Simuler des mises à jour en temps réel (toutes les 8-15 secondes)
  useEffect(() => {
    const interval = setInterval(() => {
      // Ajouter entre 15€ et 85€ aléatoirement
      const newAmount = Math.floor(Math.random() * 70) + 15;
      setTotalSavings(prev => prev + newAmount);
      setIsAnimating(true);
      
      // Parfois ajouter un nouveau client
      if (Math.random() > 0.7) {
        setClientCount(prev => prev + 1);
      }
      
      setTimeout(() => setIsAnimating(false), 1500);
    }, Math.random() * 7000 + 8000); // Entre 8 et 15 secondes
    
    return () => clearInterval(interval);
  }, []);
  
  // Animation fluide du compteur
  useEffect(() => {
    const duration = 1000;
    const startValue = displaySavings;
    const endValue = totalSavings;
    const startTime = Date.now();
    
    const animate = () => {
      const now = Date.now();
      const progress = Math.min((now - startTime) / duration, 1);
      const easeOut = 1 - Math.pow(1 - progress, 3);
      const current = Math.floor(startValue + (endValue - startValue) * easeOut);
      
      setDisplaySavings(current);
      
      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };
    
    requestAnimationFrame(animate);
  }, [totalSavings]);
  
  const formatNumber = (num) => {
    return num.toLocaleString('fr-FR');
  };
  
  return (
    <section className="py-16 px-4 relative overflow-hidden bg-gradient-to-b from-void via-gray-900/50 to-void">
      <CircuitWatermark />
      
      {/* Glow effect background */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <div 
          className={`w-96 h-96 rounded-full blur-3xl transition-all duration-1000 ${
            isAnimating ? 'bg-cyan-400/20 scale-110' : 'bg-cyan-400/10 scale-100'
          }`}
        />
      </div>
      
      <div className="max-w-4xl mx-auto relative z-10">
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center"
        >
          {/* Live indicator */}
          <div className="flex items-center justify-center gap-2 mb-6">
            <span className={`w-2 h-2 rounded-full ${isAnimating ? 'bg-success animate-ping' : 'bg-success animate-pulse'}`} />
            <span className="text-success text-xs font-mono uppercase tracking-wider">Live • Économies en temps réel</span>
          </div>
          
          {/* Main counter */}
          <div className="relative inline-block">
            {/* Glow frame */}
            <div 
              className={`absolute -inset-4 rounded-2xl transition-all duration-500 ${
                isAnimating 
                  ? 'bg-cyan-400/20 shadow-[0_0_60px_rgba(0,245,255,0.4)]' 
                  : 'bg-cyan-400/5 shadow-[0_0_30px_rgba(0,245,255,0.2)]'
              }`}
            />
            
            {/* Counter box */}
            <div 
              className={`relative px-8 py-6 rounded-xl border-2 transition-all duration-300 ${
                isAnimating 
                  ? 'border-cyan-400 bg-cyan-400/10' 
                  : 'border-cyan-400/50 bg-void/80'
              }`}
              style={{
                boxShadow: isAnimating 
                  ? '0 0 40px rgba(0, 245, 255, 0.4), inset 0 0 30px rgba(0, 245, 255, 0.1)'
                  : '0 0 20px rgba(0, 245, 255, 0.2)'
              }}
            >
              {/* Corner accents */}
              <div className="absolute top-0 left-0 w-4 h-4 border-t-2 border-l-2 border-cyan-400 rounded-tl" />
              <div className="absolute top-0 right-0 w-4 h-4 border-t-2 border-r-2 border-cyan-400 rounded-tr" />
              <div className="absolute bottom-0 left-0 w-4 h-4 border-b-2 border-l-2 border-cyan-400 rounded-bl" />
              <div className="absolute bottom-0 right-0 w-4 h-4 border-b-2 border-r-2 border-cyan-400 rounded-br" />
              
              <p className="text-gray-400 text-sm font-mono mb-2">// ÉCONOMIES_TOTALES_GÉNÉRÉES</p>
              
              <div className="flex items-baseline justify-center gap-2">
                <motion.span 
                  className="font-orbitron text-5xl md:text-7xl font-black text-white"
                  style={{ 
                    textShadow: isAnimating 
                      ? '0 0 30px rgba(0, 245, 255, 0.8), 0 0 60px rgba(0, 245, 255, 0.4)' 
                      : '0 0 20px rgba(0, 245, 255, 0.5)' 
                  }}
                  animate={isAnimating ? { scale: [1, 1.02, 1] } : {}}
                  transition={{ duration: 0.3 }}
                >
                  {formatNumber(displaySavings)}
                </motion.span>
                <span 
                  className="font-orbitron text-3xl md:text-4xl font-bold text-cyan-400"
                  style={{ textShadow: '0 0 15px rgba(0, 245, 255, 0.6)' }}
                >
                  €
                </span>
              </div>
              
              {/* Scanning line effect when animating */}
              {isAnimating && (
                <motion.div
                  className="absolute inset-0 overflow-hidden rounded-xl pointer-events-none"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                >
                  <motion.div
                    className="absolute w-full h-1 bg-gradient-to-r from-transparent via-cyan-400 to-transparent"
                    initial={{ y: '-100%' }}
                    animate={{ y: '500%' }}
                    transition={{ duration: 1, ease: 'linear' }}
                  />
                </motion.div>
              )}
            </div>
          </div>
          
          {/* Sub stats */}
          <div className="flex flex-wrap items-center justify-center gap-8 mt-8">
            <div className="text-center">
              <p className="font-orbitron text-2xl md:text-3xl font-bold text-white">
                {formatNumber(clientCount)}
              </p>
              <p className="text-gray-500 text-sm font-mono">clients satisfaits</p>
            </div>
            <div className="w-px h-12 bg-gray-700 hidden md:block" />
            <div className="text-center">
              <p className="font-orbitron text-2xl md:text-3xl font-bold text-success">
                ~{Math.round(displaySavings / clientCount)}€
              </p>
              <p className="text-gray-500 text-sm font-mono">économie moyenne</p>
            </div>
            <div className="w-px h-12 bg-gray-700 hidden md:block" />
            <div className="text-center">
              <p className="font-orbitron text-2xl md:text-3xl font-bold text-magenta-400">
                +{Math.floor(Math.random() * 8) + 3}
              </p>
              <p className="text-gray-500 text-sm font-mono">audits cette heure</p>
            </div>
          </div>
          
          {/* Badge Toulouse - 15px sous le bloc stats */}
          <div className="mt-4 flex justify-center">
            <ToulouseSignature variant="hero" />
          </div>
          
          {/* CTA */}
          <motion.div 
            className="mt-10"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
          >
            <Link to="/register">
              <Button 
                className="bg-cyan-400 text-black hover:bg-cyan-300 font-orbitron text-sm uppercase tracking-wider px-8 py-6 pulse-neon"
              >
                <TrendingUp className="w-5 h-5 mr-2" />
                Rejoindre les économiseurs
              </Button>
            </Link>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

// Legal Reassurance Section
const LegalReassurance = () => (
  <section className="py-12 px-4 border-t border-gray-800" data-testid="legal-reassurance">
    <div className="max-w-4xl mx-auto">
      <div className="glass-card p-6 border border-orange-500/30 bg-orange-500/5">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-full bg-orange-500/20 flex items-center justify-center flex-shrink-0">
            <Shield className="w-6 h-6 text-orange-400" />
          </div>
          <div>
            <h3 className="font-orbitron text-orange-400 font-bold mb-2">🛡️ Droit de Rétractation - 14 jours</h3>
            <p className="text-gray-400 text-sm leading-relaxed">
              Conformément aux articles L221-18 et suivants du Code de la consommation, vous disposez d'un délai de 
              <strong className="text-orange-300"> 14 jours </strong> 
              pour exercer votre droit de rétractation sans avoir à justifier de motifs ni à payer de pénalités.
              <Link to="/cgv" className="text-cyan-400 hover:underline ml-1">En savoir plus →</Link>
            </p>
            {/* V1.2 : téléchargement formulaire retiré de la landing publique.
                Le formulaire de rétractation est désormais accessible uniquement
                aux clients authentifiés depuis leur tableau de bord. */}
            <div className="mt-3 flex flex-wrap gap-3">
              <span className="text-gray-600 text-xs flex items-center gap-1">
                <CheckCircle className="w-3 h-3 text-success" />
                Paiement 100% sécurisé par Stripe
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

// Footer
const Footer = () => {
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  
  return (
  <footer className="border-t border-gray-800 py-12 px-4 relative" data-testid="footer">
    <CircuitWatermark />
    <div className="max-w-6xl mx-auto relative z-10">
      <div className="grid md:grid-cols-4 gap-8 mb-8">
        <div className="md:col-span-1">
          <img src={LOGO_URL} alt="L'Effaceur" className="h-28 md:h-36 opacity-70 hover:opacity-100 transition-opacity"
            style={{ filter: 'drop-shadow(0 0 15px rgba(0, 245, 255, 0.3))' }} data-testid="footer-logo" />
          <p className="text-gray-600 font-mono text-xs mt-4">Le bug qui nettoie vos prélèvements.</p>
        </div>
        <div>
          <h5 className="font-orbitron text-white text-sm font-bold mb-4 uppercase tracking-wider">Navigation</h5>
          <ul className="space-y-2 text-sm">
            <li><button onClick={scrollToTop} className="text-gray-400 hover:text-cyan-400 transition-colors cursor-pointer">Accueil ↑</button></li>
            <li><Link to="/login" className="text-gray-400 hover:text-cyan-400 transition-colors">Connexion</Link></li>
            <li><Link to="/register" className="text-gray-400 hover:text-cyan-400 transition-colors">Inscription</Link></li>
            <li><Link to="/blog" className="text-gray-400 hover:text-cyan-400 transition-colors">Blog & Guides</Link></li>
          </ul>
        </div>
        <div>
          <h5 className="font-orbitron text-white text-sm font-bold mb-4 uppercase tracking-wider">Légal</h5>
          <ul className="space-y-2 text-sm">
            <li><Link to="/mentions-legales" className="text-gray-400 hover:text-cyan-400 transition-colors">Mentions Légales</Link></li>
            <li><Link to="/cgv" className="text-gray-400 hover:text-cyan-400 transition-colors">CGV</Link></li>
            <li><Link to="/confidentialite" className="text-gray-400 hover:text-cyan-400 transition-colors">Confidentialité</Link></li>
            <li>
              <a 
                href={`${process.env.REACT_APP_BACKEND_URL}/api/legal/withdrawal-form`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-orange-400 transition-colors flex items-center gap-1"
              >
                Formulaire Rétractation
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </a>
            </li>
          </ul>
        </div>
        <div>
          <h5 className="font-orbitron text-white text-sm font-bold mb-4 uppercase tracking-wider">Contact</h5>
          <ul className="space-y-3 text-sm">
            <li>
              <a href="https://www.google.com/search?newwindow=1&sca_esv=dc45e1216515a3e4&sxsrf=ANbL-n4mUhR2YM2gtXT32puTlS0ATAxLiQ%3A1770108835540&q=Localia%20Web&stick=H4sIAAAAAAAAAONgU1IxqLCwNE1NTU4xSTU1MkwyS0mxMqhIS0u1MDFINDSzTDMxNjJfxMrtk5-cmJOZqBCemgQAGOxJCzYAAAA&mat=CVOC6KnKFFFg&ved=2ahUKEwj1pOyi-bySAxVH_rsIHQlxHU8QrMcEegQIDxAC" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-gray-400 hover:text-cyan-400 transition-colors">
                <MapPin className="w-4 h-4 text-cyan-400" />Nous trouver
              </a>
            </li>
            <li>
              <a href="mailto:leffaceur@effaceurpro.fr" className="flex items-center gap-2 text-gray-400 hover:text-cyan-400 transition-colors">
                <Mail className="w-4 h-4 text-cyan-400" />leffaceur@effaceurpro.fr
              </a>
            </li>
            <li>
              <a href="https://g.page/r/CSdDnxYKhP4PEBM/review" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-gray-400 hover:text-yellow-400 transition-colors" data-testid="google-reviews-link">
                <svg className="w-4 h-4 text-yellow-400" viewBox="0 0 24 24" fill="currentColor"><path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"/></svg>
                Donnez-nous votre avis
              </a>
            </li>
          </ul>
          <div className="flex gap-4 mt-6">
            <a href="https://www.instagram.com/localia_web" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-gray-800 flex items-center justify-center text-gray-400 hover:text-magenta-400 hover:bg-magenta-400/10 transition-all" title="Instagram"><Instagram className="w-5 h-5" /></a>
            <a href="https://www.tiktok.com/@localia_web" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-gray-800 flex items-center justify-center text-gray-400 hover:text-magenta-400 hover:bg-magenta-400/10 transition-all" title="TikTok">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/></svg>
            </a>
          </div>
        </div>
      </div>
      <div className="border-t border-gray-800 pt-6 flex flex-col md:flex-row justify-between items-center gap-4">
        <p className="text-gray-500 text-xs font-mono">© 2025 L'EFFACEUR - Tous droits réservés</p>
        <ToulouseSignature variant="footer" />
      </div>
    </div>
  </footer>
  );
};

// Visitor Counter
const VisitorCounter = () => {
  const [count, setCount] = useState(0);
  useEffect(() => {
    const fetchCount = async () => {
      try { const res = await axios.get(`${API}/stats/visitors`); setCount(res.data.visitors); } catch { setCount(1337); }
    };
    fetchCount();
  }, []);
  return (
    <div className="fixed bottom-4 right-4 z-50" data-testid="visitor-counter">
      <div className="counter-terminal text-xs bg-void/80 backdrop-blur-md border border-gray-700 px-4 py-2">
        //_AGENTS_ACTIFS: <span className="text-magenta-400">{String(count).padStart(4, '0')}</span>
      </div>
    </div>
  );
};

// Main Landing Page
export default function LandingPage() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [pricing, setPricing] = useState(null);

  useEffect(() => {
    axios.get(`${API}/packs/pricing`).then(r => setPricing(r.data)).catch(() => {});
  }, []);

  const launchActive = pricing?.launch_active ?? true;
  const pD = pricing?.packs?.mission_demenagement;
  const pH = pricing?.packs?.mission_heritage;

  const packs = [
    {
      id: 'mission_audit',
      title: 'Mission Audit',
      subtitle: '// POUVOIR_D\'ACHAT',
      price: '0€',
      priceStrike: null,
      suffix: 'à l\'avance',
      description: "Audit gratuit de vos relevés bancaires. Vous payez seulement si on vous fait économiser : 20% des économies réelles, prélevés après confirmation. Sans économies, sans frais.",
      features: ['Audit 100% gratuit (CSV ou PDF)', 'Détection abonnements oubliés', 'Comparaison prix marché', 'Résiliations en 1 clic', 'Vous ne payez que si on vous fait économiser', 'Commission 20% sur les économies réelles']
    },
    {
      id: 'mission_demenagement',
      title: 'Mission Déménagement',
      subtitle: '// NOUVELLE_VIE',
      price: pD ? `${pD.price.toFixed(0)}€` : '129€',
      priceStrike: (launchActive && pD?.price_normal && pD.price_normal !== pD.price) ? `${pD.price_normal.toFixed(0)}€` : null,
      suffix: 'forfait unique',
      description: "Vous déménagez ? On résilie l'ancienne adresse (énergie, internet, assurance, box, courrier) et on ouvre/optimise les nouveaux contrats. Zéro paperasse, zéro coupure.",
      features: ['Résiliation anciens contrats', 'Ouverture nouveaux contrats', 'Optimisation tarifs (vs marché)', 'Suivi VIP pendant 30 jours', 'Forfait fixe, pas de commission']
    },
    {
      id: 'mission_heritage',
      title: 'Mission Héritage',
      subtitle: '// SUCCESSION',
      price: pH ? `${pH.price.toFixed(0)}€` : '149€',
      priceStrike: (launchActive && pH?.price_normal && pH.price_normal !== pH.price) ? `${pH.price_normal.toFixed(0)}€` : null,
      suffix: 'forfait complet',
      description: "Vous venez de perdre un proche ? On clôture tous ses abonnements et contrats (banque, télécom, énergie, assurances, mutuelle, presse) et on remet à votre notaire un dossier complet prêt à signer.",
      features: ['Inventaire complet des contrats', 'Résiliation post-décès', 'Dossier notaire prêt à l\'usage', 'Accompagnement dédié 60 jours', 'Forfait fixe, pas de commission']
    }
  ];
  
  return (
    <div className="min-h-screen bg-void scanlines overflow-x-hidden">
      {/* SEO Schema.org JSON-LD */}
      <StructuredData type="organization" />
      <StructuredData type="faq" />
      <StructuredData type="product" pack="audit" />
      <StructuredData type="product" pack="demenagement" />
      <StructuredData type="product" pack="heritage" />
      <CircuitHeader />
      <MobileMenu isOpen={mobileMenuOpen} setIsOpen={setMobileMenuOpen} />
      
      {/* Header - Fixed, optimized for mobile */}
      <header className="fixed top-0 left-0 right-0 z-40 bg-void/90 backdrop-blur-md border-b border-gray-800/50">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          {/* Logo Text - Left */}
          <Link to="/" className="flex-shrink-0" data-testid="header-logo">
            <span className="font-orbitron text-2xl md:text-3xl font-black tracking-wider">
              <span className="text-cyan-400">L'</span>
              <span className="text-white">EFFACEUR</span>
            </span>
          </Link>
          
          {/* Desktop Nav - Hidden on mobile */}
          <nav className="hidden md:flex items-center gap-4">
            <a href="#pricing" className="text-gray-400 hover:text-cyan-400 font-mono text-sm">Tarifs</a>
            <Link to="/login" className="text-gray-300 hover:text-cyan-400 font-mono text-sm">Connexion</Link>
            <Link to="/register">
              <Button className="bg-cyan-400 text-black hover:bg-cyan-300 font-orbitron text-xs uppercase">Commencer</Button>
            </Link>
          </nav>
          
          {/* Mobile Nav - Right side */}
          <div className="flex md:hidden items-center gap-3">
            <Link to="/register">
              <Button className="bg-cyan-400 text-black hover:bg-cyan-300 font-orbitron text-xs uppercase px-4 py-2">Commencer</Button>
            </Link>
            <button onClick={() => setMobileMenuOpen(true)} className="text-gray-400 p-2" data-testid="mobile-menu-btn">
              <Menu className="w-6 h-6" />
            </button>
          </div>
        </div>
      </header>
      
      {/* Hero Section */}
      <section className="min-h-screen flex flex-col items-center justify-center px-4 pt-20 relative overflow-hidden" data-testid="hero-section">
        <CircuitHeroBackground />
        <CircuitWatermark />
        <CircuitSideBorder side="left" />
        <CircuitSideBorder side="right" />
        <CircuitCorner position="top-left" />
        <CircuitCorner position="top-right" />
        <div className="absolute top-1/4 left-10 w-2 h-2 bg-cyan-400 particle" style={{ animationDelay: '0s' }} />
        <div className="absolute top-1/3 right-20 w-3 h-3 bg-magenta-400 particle" style={{ animationDelay: '0.5s' }} />
        
        <div className="max-w-5xl mx-auto text-center relative z-10">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
            <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 0.6 }} className="mb-8">
              <img src={LOGO_URL} alt="L'Effaceur" className="h-32 sm:h-40 md:h-48 lg:h-56 mx-auto pulse-neon"
                style={{ filter: 'drop-shadow(0 0 30px rgba(0, 245, 255, 0.6))' }} data-testid="hero-logo" />
            </motion.div>
            
            <p className="font-mono text-magenta-400 text-sm md:text-base mb-6 tracking-wider">// SYSTÈME_DE_RÉSILIATION_AUTOMATISÉ</p>
            
            <h1 className="font-orbitron text-2xl sm:text-3xl md:text-5xl lg:text-6xl font-black text-white mb-6 leading-tight px-4">
              L'<span className="text-cyan-400 glitch-text">Automate</span> qui désintègre
              <br className="hidden sm:block" />
              <span className="block sm:inline"> vos contrats et abonnements inutiles</span>
            </h1>
            
            <p className="font-mono text-gray-500 text-sm mb-4">Le bug qui nettoie vos prélèvements.</p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-6">
              <Link to="/register" data-testid="cta-main">
                <Button className="bg-cyan-400 text-black hover:bg-cyan-300 font-orbitron text-sm uppercase tracking-wider px-8 py-6 pulse-neon w-full sm:w-auto">
                  Commencer maintenant<ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              <a href="#pricing" data-testid="cta-pricing">
                <Button variant="outline" className="border-magenta-400 text-magenta-400 hover:bg-magenta-400 hover:text-white font-orbitron text-sm uppercase tracking-wider px-8 py-6 w-full sm:w-auto">
                  Voir les Packs<ChevronRight className="w-5 h-5 ml-2" />
                </Button>
              </a>
            </div>
            
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }} className="flex flex-col items-center justify-center gap-3 text-center">
              <div className="flex items-center gap-2 text-gray-400 text-sm">
                <Zap className="w-4 h-4 text-success" />
                <span>En moyenne, nos clients économisent <span className="text-success font-bold">600€/an</span></span>
              </div>
              
              {/* Badge IA 24/7 - Hero */}
              <div className="flex items-center gap-2 text-success/80 text-xs mt-2 px-3 py-1.5 bg-success/10 border border-success/30 rounded-full animate-pulse">
                <Bot className="w-4 h-4" />
                <span>Assistant IA disponible 24/7 dans votre Dashboard</span>
              </div>
            </motion.div>
          </motion.div>
        </div>
        <CircuitSectionDivider invert={true} />
      </section>
      
      {/* Stats Section */}
      <section className="py-20 px-4 border-y border-gray-800 relative overflow-hidden">
        <CircuitWatermark />
        <CircuitSideBorder side="left" />
        <CircuitSideBorder side="right" />
        <CircuitAccent position="left" delay={0} />
        <CircuitAccent position="right" delay={0.5} />
        <CircuitCorner position="bottom-left" />
        <CircuitCorner position="bottom-right" />
        <div className="max-w-6xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-8 relative z-10">
          {[
            { value: "0 démarche", label: "Votre paperasse entièrement déléguée", icon: FileText },
            { value: "30%", label: "Des Français ont des abonnements inutiles", icon: Users },
            { value: "48h", label: "Purge automatique de vos données", icon: Shield },
            { value: "100%", label: "Taux de satisfaction", icon: Star }
          ].map((stat, idx) => (
            <motion.div key={idx} initial={{ opacity: 0, scale: 0.8 }} whileInView={{ opacity: 1, scale: 1 }} transition={{ delay: idx * 0.1 }} className="text-center">
              <stat.icon className="w-8 h-8 text-cyan-400 mx-auto mb-4" />
              <div className="font-orbitron text-2xl md:text-4xl font-black text-white mb-2">{stat.value}</div>
              <div className="text-gray-400 text-xs md:text-sm">{stat.label}</div>
            </motion.div>
          ))}
        </div>
        <CircuitSectionDivider invert={true} />
      </section>
      
      {/* Savings Counter - Social Proof (placé en haut de Pricing) */}
      <SavingsCounter />

      {/* Comment ça marche - 4 étapes simples */}
      <section id="how-it-works" className="py-16 px-4 relative overflow-hidden border-t border-cyan-500/10">
        <div className="max-w-6xl mx-auto relative z-10">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-12">
            <p className="font-mono text-magenta-400 text-xs mb-3 tracking-wider">// COMMENT_ÇA_MARCHE</p>
            <h2 className="font-orbitron text-3xl md:text-4xl font-bold text-white mb-3">
              4 étapes. <span className="text-cyan-400">Zéro paperasse.</span>
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">Vous nous mandatez, on s'occupe du reste. Vous suivez tout depuis votre tableau de bord.</p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {[
              {
                num: '01',
                title: 'Choisissez votre Mission',
                desc: 'Audit (gratuit), Déménagement (129€) ou Héritage (149€). Selon votre besoin.',
                color: 'cyan',
                hex: '#00F5FF',
                // Icon: 3 cartes empilées avec curseur (sélection)
                svg: (
                  <svg viewBox="0 0 64 64" className="w-12 h-12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="square">
                    <rect x="8" y="14" width="34" height="22" rx="1" opacity="0.4" />
                    <rect x="13" y="19" width="34" height="22" rx="1" opacity="0.7" />
                    <rect x="18" y="24" width="34" height="22" rx="1" />
                    <line x1="24" y1="32" x2="44" y2="32" />
                    <line x1="24" y1="38" x2="38" y2="38" />
                    <circle cx="48" cy="48" r="3" fill="currentColor" />
                    <line x1="48" y1="48" x2="56" y2="56" />
                  </svg>
                ),
              },
              {
                num: '02',
                title: 'Signez le mandat',
                desc: 'Vous nous donnez l\'autorisation de résilier en votre nom. 100% en ligne, en 2 minutes.',
                color: 'magenta',
                hex: '#FF00FF',
                // Icon: doc avec stylo signature
                svg: (
                  <svg viewBox="0 0 64 64" className="w-12 h-12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="square">
                    <path d="M14 8 L42 8 L52 18 L52 56 L14 56 Z" />
                    <path d="M42 8 L42 18 L52 18" />
                    <line x1="20" y1="26" x2="46" y2="26" opacity="0.5" />
                    <line x1="20" y1="32" x2="46" y2="32" opacity="0.5" />
                    {/* Signature curve */}
                    <path d="M20 44 Q26 36, 30 44 T40 44" strokeWidth="2.5" />
                    <circle cx="42" cy="44" r="1.5" fill="currentColor" />
                  </svg>
                ),
              },
              {
                num: '03',
                title: 'On résilie pour vous',
                desc: 'Notre équipe contacte chaque organisme par email + LRAR. Vous suivez en temps réel.',
                color: 'cyan',
                hex: '#00F5FF',
                // Icon: nœud central avec connexions (broadcast)
                svg: (
                  <svg viewBox="0 0 64 64" className="w-12 h-12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="square">
                    {/* Central hub */}
                    <circle cx="32" cy="32" r="6" fill="currentColor" opacity="0.2" />
                    <circle cx="32" cy="32" r="4" />
                    {/* 4 nœuds périphériques */}
                    <circle cx="12" cy="12" r="3" fill="currentColor" />
                    <circle cx="52" cy="12" r="3" fill="currentColor" />
                    <circle cx="12" cy="52" r="3" fill="currentColor" />
                    <circle cx="52" cy="52" r="3" fill="currentColor" />
                    {/* Connexions */}
                    <line x1="14" y1="14" x2="28" y2="28" opacity="0.6" />
                    <line x1="50" y1="14" x2="36" y2="28" opacity="0.6" />
                    <line x1="14" y1="50" x2="28" y2="36" opacity="0.6" />
                    <line x1="50" y1="50" x2="36" y2="36" opacity="0.6" />
                  </svg>
                ),
              },
              {
                num: '04',
                title: 'Vous économisez',
                desc: 'Confirmation à chaque étape. Preuves de résiliation téléchargeables. Vous gagnez du temps et de l\'argent.',
                color: 'success',
                hex: '#00FF94',
                // Icon: courbe ascendante + flèche euro
                svg: (
                  <svg viewBox="0 0 64 64" className="w-12 h-12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="square">
                    {/* Axes */}
                    <line x1="10" y1="54" x2="56" y2="54" opacity="0.3" />
                    <line x1="10" y1="10" x2="10" y2="54" opacity="0.3" />
                    {/* Courbe ascendante */}
                    <path d="M14 48 L24 40 L34 32 L44 22 L52 14" strokeWidth="2.5" />
                    {/* Points */}
                    <circle cx="14" cy="48" r="2" fill="currentColor" />
                    <circle cx="24" cy="40" r="2" fill="currentColor" />
                    <circle cx="34" cy="32" r="2" fill="currentColor" />
                    <circle cx="44" cy="22" r="2" fill="currentColor" />
                    <circle cx="52" cy="14" r="2.5" fill="currentColor" />
                    {/* Flèche montée */}
                    <path d="M48 18 L52 14 L56 18" />
                  </svg>
                ),
              },
            ].map((step, idx) => (
              <motion.div
                key={step.num}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: idx * 0.1 }}
                className="relative"
              >
                <div className={`glass-card p-6 border ${step.color === 'cyan' ? 'border-cyan-500/30' : step.color === 'magenta' ? 'border-magenta-400/30' : 'border-success/30'} h-full hover:scale-[1.02] transition-transform duration-300`}>
                  <div
                    className={`mb-3 ${step.color === 'cyan' ? 'text-cyan-400' : step.color === 'magenta' ? 'text-magenta-400' : 'text-success'}`}
                    style={{ filter: `drop-shadow(0 0 8px ${step.hex}55)` }}
                  >
                    {step.svg}
                  </div>
                  <div className={`font-orbitron text-xs font-mono mb-2 ${step.color === 'cyan' ? 'text-cyan-400' : step.color === 'magenta' ? 'text-magenta-400' : 'text-success'}`}>
                    ÉTAPE {step.num}
                  </div>
                  <h3 className="font-orbitron text-base font-bold text-white mb-2">{step.title}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{step.desc}</p>
                </div>
                {/* Flèche entre étapes (desktop only, pas après la dernière) */}
                {idx < 3 && (
                  <div className="hidden md:flex absolute top-1/2 -right-3 -translate-y-1/2 z-10">
                    <ArrowRight className="w-5 h-5 text-cyan-400/40" />
                  </div>
                )}
              </motion.div>
            ))}
          </div>

          <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} className="text-center mt-12">
            <a href="#pricing">
              <Button className="bg-cyan-400 text-black hover:bg-cyan-300 font-orbitron text-sm uppercase tracking-wider px-8 py-5">
                Voir les Packs <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </a>
          </motion.div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 px-4 relative overflow-hidden" data-testid="pricing-section">
        <CircuitWatermark />
        <CircuitSideBorder side="left" />
        <CircuitSideBorder side="right" />
        <CircuitCorner position="top-left" />
        <CircuitCorner position="top-right" />
        <div className="max-w-6xl mx-auto relative z-10">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} className="text-center mb-16">
            <h2 className="font-orbitron text-3xl md:text-4xl font-bold text-white mb-4">CHOISISSEZ VOTRE <span className="text-cyan-400">MISSION</span></h2>
            <p className="text-gray-400 max-w-2xl mx-auto">Trois formules adaptées à vos besoins de résiliation</p>
          </motion.div>
          <div className="grid md:grid-cols-3 gap-6 md:gap-8">
            {packs.map((pack, idx) => <PricingCard key={pack.id} pack={pack} isPopular={idx === 1} />)}
          </div>
        </div>
      </section>
      
      {/* === PROMESSE DE NEUTRALITÉ === */}
      <section className="py-20 px-4 relative overflow-hidden bg-gradient-to-b from-transparent via-cyan-950/20 to-transparent" data-testid="neutrality-section">
        <CircuitWatermark />
        <div className="max-w-4xl mx-auto relative z-10">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} className="text-center mb-10">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-cyan-400/10 border border-cyan-400/30 mb-6">
              <Shield className="w-4 h-4 text-cyan-400" />
              <span className="text-cyan-400 text-xs font-orbitron uppercase tracking-wider">Notre Engagement</span>
            </div>
            <h2 className="font-orbitron text-3xl md:text-4xl font-bold text-white mb-4">
              PROMESSE DE <span className="text-cyan-400">NEUTRALITÉ</span>
            </h2>
            <p className="text-xl text-cyan-400 font-medium italic" style={{ textShadow: '0 0 20px rgba(0, 245, 255, 0.3)' }}>
              "Votre économie est notre seule boussole."
            </p>
          </motion.div>
          
          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                icon: Target,
                title: "Indépendance Totale",
                description: "Aucune commission d'affiliation, aucun partenariat opaque avec les fournisseurs. Nos experts toulousains agissent uniquement dans votre intérêt, sous mandat juridique Art. 1984 C. Civ."
              },
              {
                icon: Eye,
                title: "Transparence",
                description: "Tarif unique affiché avant signature du mandat. Chaque démarche est horodatée, traçable et certifiée. Aucun frais caché de notre côté — les éventuels frais de résiliation appliqués par vos fournisseurs restent à votre charge."
              },
              {
                icon: Heart,
                title: "Priorité Client",
                description: "Un mandat suffit à tout déléguer : résiliations, transferts et succession. Nos experts pilotent vos contrats jusqu'à confirmation écrite — vous n'intervenez plus."
              }
            ].map((item, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.15 }}
                className="glass-card p-6 border border-cyan-400/20 hover:border-cyan-400/50 transition-all group text-center"
              >
                <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-cyan-400/10 flex items-center justify-center group-hover:bg-cyan-400/20 transition-colors">
                  <item.icon className="w-7 h-7 text-cyan-400" />
                </div>
                <h3 className="font-orbitron text-lg font-bold text-white mb-3">{item.title}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      
      {/* Features Section */}
      <section className="py-20 px-4 bg-accent/30 relative overflow-hidden">
        <CircuitWatermark />
        {/* Micro-circuits aux coins */}
        <div className="absolute top-10 left-10 w-20 h-20 opacity-20 pointer-events-none">
          <svg className="w-full h-full" viewBox="0 0 80 80">
            <path d="M0 40 L20 40 L30 20 L50 20 L60 40 L80 40" stroke="#00F5FF" strokeWidth="1" fill="none">
              <animate attributeName="stroke-dasharray" from="0 200" to="200 0" dur="3s" repeatCount="indefinite" />
            </path>
            <circle cx="30" cy="20" r="3" fill="#00F5FF"><animate attributeName="opacity" values="0.3;0.8;0.3" dur="2s" repeatCount="indefinite" /></circle>
          </svg>
        </div>
        <div className="absolute bottom-10 right-10 w-20 h-20 opacity-20 pointer-events-none">
          <svg className="w-full h-full" viewBox="0 0 80 80">
            <path d="M80 40 L60 40 L50 60 L30 60 L20 40 L0 40" stroke="#00F5FF" strokeWidth="1" fill="none">
              <animate attributeName="stroke-dasharray" from="0 200" to="200 0" dur="3s" repeatCount="indefinite" />
            </path>
            <circle cx="50" cy="60" r="3" fill="#00F5FF"><animate attributeName="opacity" values="0.3;0.8;0.3" dur="2s" repeatCount="indefinite" /></circle>
          </svg>
        </div>
        <div className="max-w-6xl mx-auto relative z-10">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} className="text-center mb-16">
            <h2 className="font-orbitron text-3xl md:text-4xl font-bold text-white mb-4">TECHNOLOGIE <span className="text-cyan-400">MILITAIRE</span></h2>
            <p className="text-gray-400 max-w-2xl mx-auto">Une infrastructure conçue pour protéger vos données sensibles</p>
          </motion.div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { icon: Shield, title: "Protocole Zéro Trace", description: "Vos documents sont chiffrés AES-256 et supprimés après 30 jours." },
              { icon: Bot, title: "Automate de Résiliation", description: "Notre IA génère et envoie automatiquement les courriers de résiliation." },
              { icon: Lock, title: "Mandat de Succès", description: "Signature électronique horodatée selon l'article 1984 du Code Civil." }
            ].map((feature, idx) => (
              <motion.div key={idx} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.1 }}
                className="glass-card p-8 border border-gray-800 hover:border-cyan-400/50 transition-all group relative overflow-hidden">
                {/* Circuit hover effect */}
                <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-cyan-400/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <h3 className="font-orbitron text-lg font-bold text-white mb-3">{feature.title}</h3>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      
      {/* FAQ Section */}
      <FAQSection />
      
      {/* CTA Section */}
      <section className="py-20 px-4 bg-gradient-to-b from-void to-accent/50 relative overflow-hidden">
        <CircuitWatermark />
        {/* Animated circuit lines in background */}
        <div className="absolute inset-0 pointer-events-none">
          <svg className="w-full h-full opacity-10" viewBox="0 0 1920 400" preserveAspectRatio="xMidYMid slice">
            <path d="M0 200 L400 200 L450 150 L600 150 L650 200 L960 200" stroke="#00F5FF" strokeWidth="1" fill="none">
              <animate attributeName="stroke-dasharray" from="0 2000" to="2000 0" dur="5s" repeatCount="indefinite" />
            </path>
            <path d="M1920 200 L1520 200 L1470 250 L1320 250 L1270 200 L960 200" stroke="#00F5FF" strokeWidth="1" fill="none">
              <animate attributeName="stroke-dasharray" from="0 2000" to="2000 0" dur="5s" repeatCount="indefinite" />
            </path>
          </svg>
        </div>
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <motion.div initial={{ opacity: 0, scale: 0.95 }} whileInView={{ opacity: 1, scale: 1 }} className="glass-card p-8 md:p-12 border border-cyan-400/30 relative overflow-hidden group">
            {/* Corner circuit accents */}
            <div className="absolute top-0 left-0 w-16 h-16 opacity-30 group-hover:opacity-60 transition-opacity">
              <svg viewBox="0 0 64 64"><path d="M0 0 L0 30 L30 30" stroke="#00F5FF" strokeWidth="1" fill="none" /></svg>
            </div>
            <div className="absolute bottom-0 right-0 w-16 h-16 opacity-30 group-hover:opacity-60 transition-opacity">
              <svg viewBox="0 0 64 64"><path d="M64 64 L64 34 L34 34" stroke="#00F5FF" strokeWidth="1" fill="none" /></svg>
            </div>
            <h2 className="font-orbitron text-2xl md:text-3xl font-bold text-white mb-4">PRÊT À REPRENDRE LE <span className="text-cyan-400">CONTRÔLE</span> ?</h2>
            <p className="text-gray-400 mb-8 max-w-xl mx-auto">Rejoignez les milliers de Français qui ont déjà récupéré leur pouvoir d'achat grâce à L'Effaceur.</p>
            <Link to="/register">
              <Button className="bg-cyan-400 text-black hover:bg-cyan-300 font-orbitron text-sm uppercase tracking-wider px-12 py-6 relative overflow-hidden group">
                <span className="relative z-10">Créer Mon Compte Gratuit<ArrowRight className="w-5 h-5 ml-2 inline" /></span>
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
      
      <LegalReassurance />
      <Footer />
      <VisitorCounter />
      
      {/* Chatbot proactif */}
      <ProactiveChatBubble 
        variant="landing" 
        onOpenChat={() => setChatOpen(true)} 
      />
      
      {/* Bouton flottant chatbot */}
      <button
        onClick={() => setChatOpen(true)}
        className="fixed bottom-4 right-4 z-50 w-14 h-14 bg-gradient-to-br from-cyan-400 to-magenta-400 rounded-full flex items-center justify-center shadow-lg hover:scale-110 transition-transform"
        style={{ boxShadow: '0 0 20px rgba(0, 245, 255, 0.4)' }}
        data-testid="chat-trigger-landing"
      >
        <Bot className="w-7 h-7 text-black" />
      </button>
      
      {/* Modal Chatbot */}
      {chatOpen && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4">
          <div 
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => setChatOpen(false)}
          />
          <div className="relative w-full max-w-md">
            <AssistantDuBug 
              isOpen={chatOpen} 
              onClose={() => setChatOpen(false)} 
              isFloating={true}
            />
          </div>
        </div>
      )}
    </div>
  );
}
